import React, { useState, useEffect, useCallback } from "react";
import { Container, Form, Button, Modal, Spinner } from "react-bootstrap";
import { simplePostCall } from "../../api/ApiServices";
import ApiConfig from "../../api/ApiConfig";
import { toast, ToastContainer } from "react-toastify";
import ScaleLoader from "react-spinners/ScaleLoader";
import { formatNumber } from "../../utils/formatNumber";
import {
  useAccounts,
  AccountSelect,
  NoAccountsWarning,
  financeAuthPayload,
} from "../../components/finance/AccountSelect";
import FinanceBreadcrumb from "../../components/finance/Breadcrumb";
import "../../Styles/yellocalfcss/Liabilities.css";

/**
 * Salaries Payable
 *
 * Accruing payroll posts a journal (Dr Salary Expense, Cr Salaries Payable) so
 * the accrue form collects both accounts. Paying it posts Dr Salaries Payable,
 * Cr Cash/Bank — hence the pay modal collects the cash account. Each row is one
 * payroll accrual for a pay period.
 *
 * Backend dependency: ApiConfig.salariesPayableList / salariesPayableCreate /
 * salariesPayablePay.
 * Row shape: { salary_payable_id, pay_period, description, amount,
 *   amount_paid, status }
 */
const SalariesPayable = () => {
  const customer_currency = (
    localStorage.getItem("customer_currency") || ""
  ).replace(/"/g, "");

  const { accounts, loading: accountsLoading } = useAccounts();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ status: "", search: "" });

  // Accrue (create) modal
  const [showAccrueModal, setShowAccrueModal] = useState(false);
  const [accrueForm, setAccrueForm] = useState(blankAccrue());
  const [accruing, setAccruing] = useState(false);

  // Pay modal
  const [showPayModal, setShowPayModal] = useState(false);
  const [activeRow, setActiveRow] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payDate, setPayDate] = useState("");
  const [payAccountId, setPayAccountId] = useState("");
  const [paying, setPaying] = useState(false);

  function blankAccrue() {
    return {
      pay_period: "",
      description: "",
      liability_account_id: "",
      expense_account_id: "",
      amount: "",
      accrual_date: new Date().toISOString().slice(0, 10),
    };
  }

  const fetchSalaries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await simplePostCall(
        ApiConfig.salariesPayableList,
        JSON.stringify({
          ...financeAuthPayload(),
          status: filters.status,
          search: filters.search,
        })
      );
      setRows(res?.result && Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Salaries fetch failed:", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filters.status, filters.search]);

  useEffect(() => {
    fetchSalaries();
  }, [fetchSalaries]);

  const balanceOf = (r) =>
    (parseFloat(r.amount) || 0) - (parseFloat(r.amount_paid) || 0);

  const totals = rows.reduce(
    (acc, r) => {
      acc.payable += parseFloat(r.amount) || 0;
      acc.paid += parseFloat(r.amount_paid) || 0;
      acc.outstanding += balanceOf(r);
      return acc;
    },
    { payable: 0, paid: 0, outstanding: 0 }
  );

  // --- Accrue ---
  const openAccrue = () => {
    setAccrueForm(blankAccrue());
    setShowAccrueModal(true);
  };

  const handleAccrueChange = (e) => {
    const { name, value } = e.target;
    setAccrueForm((prev) => ({ ...prev, [name]: value }));
  };

  const submitAccrue = async () => {
    if (
      !accrueForm.pay_period ||
      !accrueForm.amount ||
      !accrueForm.liability_account_id ||
      !accrueForm.expense_account_id ||
      !accrueForm.accrual_date
    ) {
      toast.warning(
        "Pay period, amount, accrual date, liability account and expense account are all required."
      );
      return;
    }
    setAccruing(true);
    try {
      const res = await simplePostCall(
        ApiConfig.salariesPayableCreate,
        JSON.stringify({
          ...financeAuthPayload(),
          pay_period: accrueForm.pay_period,
          description: accrueForm.description,
          liability_account_id: Number(accrueForm.liability_account_id),
          expense_account_id: Number(accrueForm.expense_account_id),
          amount: Number(accrueForm.amount),
          accrual_date: accrueForm.accrual_date,
        })
      );
      if (res?.result) {
        toast.success("Payroll accrued and posted to the ledger.");
        setShowAccrueModal(false);
        fetchSalaries();
      } else {
        toast.error(res?.message || "Accrual failed.");
      }
    } catch (err) {
      toast.error("Error accruing payroll.");
    } finally {
      setAccruing(false);
    }
  };

  // --- Pay ---
  const openPay = (row) => {
    setActiveRow(row);
    setPayAmount(String(balanceOf(row)));
    setPayDate(new Date().toISOString().slice(0, 10));
    setPayAccountId("");
    setShowPayModal(true);
  };

  const submitPay = async () => {
    if (!activeRow || !payAmount || Number(payAmount) <= 0) {
      toast.warning("Enter a payment amount greater than zero.");
      return;
    }
    if (!payAccountId) {
      toast.warning("Select the cash / bank account the payment is made from.");
      return;
    }
    setPaying(true);
    try {
      const res = await simplePostCall(
        ApiConfig.salariesPayablePay,
        JSON.stringify({
          ...financeAuthPayload(),
          salary_payable_id: activeRow.salary_payable_id,
          payment_date: payDate,
          payment_amount: Number(payAmount),
          payment_account_id: Number(payAccountId),
        })
      );
      if (res?.result) {
        toast.success("Salary payment recorded and posted to the ledger.");
        setShowPayModal(false);
        fetchSalaries();
      } else {
        toast.error(res?.message || "Payment failed.");
      }
    } catch (err) {
      toast.error("Error recording payment.");
    } finally {
      setPaying(false);
    }
  };

  const statusLabel = (s) =>
    ({
      paid: "Paid",
      partial_paid: "Partially Paid",
      open: "Open",
    }[String(s || "open").toLowerCase()] || "Open");

  const noAccounts = !accountsLoading && accounts.length === 0;

  return (
    <div className="liabilities-page">
      <ToastContainer />
      <Container fluid>
        <FinanceBreadcrumb
          items={[{ label: "Accounts Payable" }, { label: "Salaries Payable" }]}
        />
        <div className="special-header panel-white-all d-flex justify-content-between align-items-center">
          <h4 className="page-title">Salaries Payable</h4>
          <Button
            onClick={openAccrue}
            style={{ backgroundColor: "var(--color-primary)", border: "none" }}
          >
            + Accrue Payroll
          </Button>
        </div>

        <div className="lib-summary-row">
          <div className="lib-tile lib-tile-total">
            <span className="lib-tile-label">Total Payable</span>
            <span className="lib-tile-value">
              {customer_currency} {formatNumber(totals.payable)}
            </span>
          </div>
          <div className="lib-tile lib-tile-paid">
            <span className="lib-tile-label">Paid</span>
            <span className="lib-tile-value">
              {customer_currency} {formatNumber(totals.paid)}
            </span>
          </div>
          <div className="lib-tile lib-tile-outstanding">
            <span className="lib-tile-label">Outstanding</span>
            <span className="lib-tile-value">
              {customer_currency} {formatNumber(totals.outstanding)}
            </span>
          </div>
        </div>

        <div className="lib-filter-bar">
          <Form.Select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          >
            <option value="">All Statuses</option>
            <option value="open">Open</option>
            <option value="partial_paid">Partially Paid</option>
            <option value="paid">Paid</option>
          </Form.Select>
          <Form.Control
            type="text"
            placeholder="Search period or description"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          />
          <Button
            variant="secondary"
            onClick={() => setFilters({ status: "", search: "" })}
          >
            Clear
          </Button>
        </div>

        <div className="lib-table-wrap">
          <table className="lib-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Pay Period</th>
                <th>Description</th>
                <th>Amount</th>
                <th>Paid</th>
                <th>Balance</th>
                <th>Status</th>
                <th className="text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="text-center py-4">
                    <ScaleLoader color="#F4BB44" />
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-4 text-muted">
                    No accrued salaries found.
                  </td>
                </tr>
              ) : (
                rows.map((r, idx) => {
                  const bal = balanceOf(r);
                  return (
                    <tr key={r.salary_payable_id || idx}>
                      <td>{idx + 1}</td>
                      <td className="fw-semibold">{r.pay_period || "—"}</td>
                      <td>{r.description || "—"}</td>
                      <td>
                        {customer_currency} {formatNumber(r.amount)}
                      </td>
                      <td>
                        {customer_currency} {formatNumber(r.amount_paid)}
                      </td>
                      <td className="fw-bold">
                        {customer_currency} {formatNumber(bal)}
                      </td>
                      <td>{statusLabel(r.status)}</td>
                      <td className="text-center">
                        {bal > 0 && (
                          <Button
                            size="sm"
                            onClick={() => openPay(r)}
                            style={{
                              backgroundColor: "var(--color-primary)",
                              border: "none",
                            }}
                          >
                            Pay
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Container>

      {/* Accrue payroll modal */}
      <Modal
        show={showAccrueModal}
        onHide={() => !accruing && setShowAccrueModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Accrue Payroll</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {noAccounts && <NoAccountsWarning />}
          <div className="row g-3">
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>
                  Pay Period <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  name="pay_period"
                  value={accrueForm.pay_period}
                  onChange={handleAccrueChange}
                  placeholder="e.g. 2026-07"
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>
                  Amount <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  name="amount"
                  value={accrueForm.amount}
                  onChange={handleAccrueChange}
                />
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group>
                <Form.Label>
                  Salaries Payable Account (credit){" "}
                  <span className="text-danger">*</span>
                </Form.Label>
                <AccountSelect
                  accounts={accounts}
                  name="liability_account_id"
                  types={["liability"]}
                  value={accrueForm.liability_account_id}
                  onChange={handleAccrueChange}
                  placeholder="Select liability account"
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>
                  Salary Expense Account (debit){" "}
                  <span className="text-danger">*</span>
                </Form.Label>
                <AccountSelect
                  accounts={accounts}
                  name="expense_account_id"
                  types={["expense"]}
                  value={accrueForm.expense_account_id}
                  onChange={handleAccrueChange}
                  placeholder="Select expense account"
                />
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group>
                <Form.Label>
                  Accrual Date <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  name="accrual_date"
                  value={accrueForm.accrual_date}
                  onChange={handleAccrueChange}
                />
              </Form.Group>
            </div>
            <div className="col-md-12">
              <Form.Group>
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="description"
                  value={accrueForm.description}
                  onChange={handleAccrueChange}
                  placeholder="e.g. July payroll — teaching staff"
                />
              </Form.Group>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowAccrueModal(false)}
            disabled={accruing}
          >
            Cancel
          </Button>
          <Button
            onClick={submitAccrue}
            disabled={accruing || noAccounts}
            style={{ backgroundColor: "var(--color-primary)", border: "none" }}
          >
            {accruing ? <Spinner size="sm" /> : "Accrue Payroll"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Pay modal */}
      <Modal
        show={showPayModal}
        onHide={() => !paying && setShowPayModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Record Salary Payment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {activeRow && (
            <div className="mb-3 p-2 rounded" style={{ background: "#f8f9fa" }}>
              <div>
                <b>{activeRow.pay_period}</b>
              </div>
              <div className="text-muted" style={{ fontSize: 13 }}>
                {activeRow.description} • Balance {customer_currency}{" "}
                {formatNumber(balanceOf(activeRow))}
              </div>
            </div>
          )}
          <Form.Group className="mb-3">
            <Form.Label>
              Payment Amount <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              min="0"
              value={payAmount}
              onChange={(e) => setPayAmount(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>
              Paid From (Cash / Bank) <span className="text-danger">*</span>
            </Form.Label>
            <AccountSelect
              accounts={accounts}
              types={["asset"]}
              value={payAccountId}
              onChange={(e) => setPayAccountId(e.target.value)}
              placeholder="Select cash / bank account"
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Payment Date</Form.Label>
            <Form.Control
              type="date"
              value={payDate}
              onChange={(e) => setPayDate(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowPayModal(false)}
            disabled={paying}
          >
            Cancel
          </Button>
          <Button
            onClick={submitPay}
            disabled={paying}
            style={{ backgroundColor: "var(--color-primary)", border: "none" }}
          >
            {paying ? <Spinner size="sm" /> : "Record Payment"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SalariesPayable;
